import dayjs from "dayjs";
import { DesignerApplication, UserFromServer, UserAccountType, AdminRole } from "../__generated__/types";

export * from "./test-query";
export * from "./test-mutation";
export * from "./option-helpers";
export * from "./ecommerce-helpers";
export * from "./route-paths";
export * from "./attribution";

// general site section padding
export const sectionPadding = {
  pt: { xs: "64px", lg: "108px" },
  pb: { xs: "64px", lg: "108px" },
  px: "32px",
};

export const hasApplicationChanged = (
  initial: DesignerApplication | null,
  update: DesignerApplication
) => {
  let hasChanged = false;

  // If initial is null, consider it changed
  if (!initial) {
    return true;
  }

  hasChanged = initial.status !== update.status;
  if (!hasChanged)
    hasChanged = initial.membershipTier !== update.membershipTier;
  if (!hasChanged)
    hasChanged =
      initial.singlePaymentItems?.length !== update.singlePaymentItems?.length;
  if (!hasChanged) {
    hasChanged =
      JSON.stringify(initial.singlePaymentItems) !==
      JSON.stringify(update.singlePaymentItems);
  }
  if (!hasChanged)
    hasChanged = initial.experience?.length !== update.experience?.length;
  if (!hasChanged) {
    hasChanged =
      JSON.stringify(initial.experience) !== JSON.stringify(update.experience);
  }
  if (!hasChanged) hasChanged = initial.education !== update.education;
  if (!hasChanged) {
    hasChanged =
      JSON.stringify(initial.education) !== JSON.stringify(update.education);
  }
  if (!hasChanged)
    hasChanged = initial.references?.length !== update.references?.length;
  if (!hasChanged) {
    hasChanged =
      JSON.stringify(initial.references) !== JSON.stringify(update.references);
  }
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.expectations !==
      update?.businessGoals?.expectations;
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.challenges !== update?.businessGoals?.challenges;
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.projectCapacity?.largeScale !==
      update?.businessGoals?.projectCapacity?.largeScale;
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.projectCapacity?.smallScale !==
      update?.businessGoals?.projectCapacity?.smallScale;
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.overview?.numberOfProjectsLastYear !==
      update?.businessGoals?.overview?.numberOfProjectsLastYear;
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.overview?.numberOfProjectsCurrentYear !==
      update?.businessGoals?.overview?.numberOfProjectsCurrentYear;
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.overview?.totalRevenueCurrentYear !==
      update?.businessGoals?.overview?.totalRevenueCurrentYear;
  if (!hasChanged)
    hasChanged =
      initial?.businessGoals?.overview?.totalRevenueLastYear !==
      update?.businessGoals?.overview?.totalRevenueLastYear;

  return hasChanged;
};

export const isEmailValid = (email: string) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailIsValid = emailPattern.test(email || "");
  return !!emailIsValid;
};

export const isPasswordValid = (password: string) => {
  const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*(\W|_)).{5,}$/;
  const passwordRegexIsValid = passwordPattern.test(password || "");
  const passwordIsValid = password?.length && password.length >= 8;
  return !!(passwordRegexIsValid && passwordIsValid);
};

export const isPhoneNumberValid = (phoneNumber: string) => {
  const phoneNumberPattern = /^\d{10}$/;
  const phoneNumberIsValid = phoneNumberPattern.test(phoneNumber || "");
  return !!phoneNumberIsValid;
};

export const isDateBeforeDate = (
  date1: string | null,
  date2: string | null
) => {
  if (!date1 || !date2) {
    return false;
  }
  const formattedDate1 = dayjs(date1);
  const formattedDate2 = dayjs(date2);
  const isBeforeDate = formattedDate1.isBefore(formattedDate2, "day");
  return !!isBeforeDate;
};

export const isDateBeforeToday = (date: string | null) => {
  if (!date) {
    return false;
  }
  const formattedDate = dayjs(date);
  const today = dayjs();
  const isBeforeToday = formattedDate.isBefore(today, "day");
  return !!isBeforeToday;
};

export const isAdminUser = (user: UserFromServer | null) => {
  const ADMIN_EMAIL_ACCOUNTS = new Set([
    "trent@homedesignguild.com",
    "trentmcdole94@gmail.com",
    "jackie@homedesignguild.com",
    "marlin@homedesignguild.com",
    "jackie@anselandanderson.com",
    "abbie@anselandanderson.com",
  ]);
  const isAdminAccountType =
    user?.accountType === UserAccountType.Admin ||
    (user as any)?.role === AdminRole.SuperAdmin ||
    (user as any)?.role === AdminRole.Admin;

  const isWhitelistedEmail =
    !!(user?.email && ADMIN_EMAIL_ACCOUNTS.has(user.email.toLowerCase()));

  return Boolean(isAdminAccountType || isWhitelistedEmail);
};
